import { ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuth } from '../../auth/hooks/useAuth';
import { ForbiddenError } from '@/lib/apiClient';
import { chatHistoryService } from '../services/chatHistoryService';
import { chatService, ChatError } from '../services/chatService';
import { useChatExport } from '../hooks/useChatExport';
import { estimateTokens } from '@/lib/tokenEstimator';
import { STREAM_FLUSH_MS, HISTORY_PAGE_LIMIT } from '@/config/appConfig';
import type { ConversationSummary, Message, StoredMessage } from '../types/chat.types';

interface ChatStoreState {
  messages: Message[];
  input: string;
  conversationId: string | null;
  isSending: boolean;
  sendError: string | null;
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isSidebarCollapsed: boolean;
  isHistoryLoading: boolean;
  historyError: string | null;
  historyNextCursor: string | null;
  isLoadingMoreHistory: boolean;
  isMessagesLoading: boolean;
  messagesError: string | null;
  tokenCount: number;
  isExhausted: boolean;
  streamingText: string;
  agentStatus: string | null;
  queuedMessage: string | null;
}

type ChatAction =
  | { type: 'SET_INPUT'; payload: string }
  | { type: 'SET_CONVERSATION_ID'; payload: string | null }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'APPEND_MESSAGE'; payload: Message }
  | { type: 'RESET_SESSION' }
  | { type: 'SET_SENDING'; payload: boolean }
  | { type: 'SET_SEND_ERROR'; payload: string | null }
  | { type: 'SET_CONVERSATIONS'; payload: ConversationSummary[] }
  | { type: 'SET_ACTIVE_CONVERSATION'; payload: string | null }
  | { type: 'TOGGLE_SIDEBAR' }
  | { type: 'SET_HISTORY_LOADING'; payload: boolean }
  | { type: 'SET_HISTORY_ERROR'; payload: string | null }
  | { type: 'SET_HISTORY_NEXT_CURSOR'; payload: string | null }
  | { type: 'SET_LOADING_MORE_HISTORY'; payload: boolean }
  | { type: 'SET_MESSAGES_LOADING'; payload: boolean }
  | { type: 'SET_MESSAGES_ERROR'; payload: string | null }
  | { type: 'SET_TOKEN_INFO'; payload: { tokenCount: number; isExhausted: boolean } }
  | { type: 'RESET_TOKEN_INFO' }
  | { type: 'APPEND_STREAMING_TEXT'; payload: string }
  | { type: 'SET_AGENT_STATUS'; payload: string | null }
  | { type: 'SET_QUEUED_MESSAGE'; payload: string | null }
  | { type: 'CLEAR_STREAMING' };

const isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768;

const initialState: ChatStoreState = {
  messages: [],
  input: '',
  conversationId: null,
  isSending: false,
  sendError: null,
  conversations: [],
  activeConversationId: null,
  isSidebarCollapsed: isMobileViewport,
  isHistoryLoading: false,
  historyError: null,
  historyNextCursor: null,
  isLoadingMoreHistory: false,
  isMessagesLoading: false,
  messagesError: null,
  tokenCount: 0,
  isExhausted: false,
  streamingText: '',
  agentStatus: null,
  queuedMessage: null,
};

const chatReducer = (state: ChatStoreState, action: ChatAction): ChatStoreState => {
  switch (action.type) {
    case 'SET_INPUT':
      return { ...state, input: action.payload };
    case 'SET_CONVERSATION_ID':
      return { ...state, conversationId: action.payload };
    case 'SET_MESSAGES':
      return { ...state, messages: action.payload };
    case 'APPEND_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
    case 'RESET_SESSION':
      return {
        ...state,
        messages: [],
        input: '',
        conversationId: null,
        isSending: false,
        sendError: null,
        isMessagesLoading: false,
        messagesError: null,
        tokenCount: 0,
        isExhausted: false,
        streamingText: '',
        agentStatus: null,
        queuedMessage: null,
      };
    case 'SET_SENDING':
      return { ...state, isSending: action.payload };
    case 'SET_SEND_ERROR':
      return { ...state, sendError: action.payload };
    case 'SET_CONVERSATIONS':
      return { ...state, conversations: action.payload };
    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.payload };
    case 'TOGGLE_SIDEBAR':
      return { ...state, isSidebarCollapsed: !state.isSidebarCollapsed };
    case 'SET_HISTORY_LOADING':
      return { ...state, isHistoryLoading: action.payload };
    case 'SET_HISTORY_ERROR':
      return { ...state, historyError: action.payload };
    case 'SET_HISTORY_NEXT_CURSOR':
      return { ...state, historyNextCursor: action.payload };
    case 'SET_LOADING_MORE_HISTORY':
      return { ...state, isLoadingMoreHistory: action.payload };
    case 'SET_MESSAGES_LOADING':
      return { ...state, isMessagesLoading: action.payload };
    case 'SET_MESSAGES_ERROR':
      return { ...state, messagesError: action.payload };
    case 'SET_TOKEN_INFO':
      return { ...state, tokenCount: action.payload.tokenCount, isExhausted: action.payload.isExhausted };
    case 'RESET_TOKEN_INFO':
      return { ...state, tokenCount: 0, isExhausted: false };
    case 'APPEND_STREAMING_TEXT':
      return { ...state, streamingText: state.streamingText + action.payload };
    case 'SET_AGENT_STATUS':
      return { ...state, agentStatus: action.payload };
    case 'SET_QUEUED_MESSAGE':
      return { ...state, queuedMessage: action.payload };
    case 'CLEAR_STREAMING':
      return { ...state, streamingText: '', agentStatus: null, queuedMessage: null };
    default:
      return state;
  }
};

const storedToMessage = (stored: StoredMessage): Message => ({
  id: stored.id,
  role: stored.role,
  content: stored.content,
  createdAt: stored.createdAt,
  isLiked: stored.isLiked,
});

const buildConversationName = (messages: Message[]): string => {
  const firstUserMessage = messages.find((message) => message.role === 'user' && message.content.trim().length > 0);

  if (!firstUserMessage) {
    return 'New conversation';
  }

  const cleanContent = firstUserMessage.content.trim();
  if (!cleanContent) {
    return 'New conversation';
  }

  return cleanContent.length > 60 ? `${cleanContent.slice(0, 57)}…` : cleanContent;
};

interface ChatContextValue extends ChatStoreState {
  setInput: (value: string) => void;
  clearSendError: () => void;
  sendMessage: (value: string) => Promise<void>;
  // streamingText and agentStatus are inherited from ChatStoreState
  toggleSidebar: () => void;
  selectConversation: (conversationId: string | null) => Promise<void>;
  startNewConversation: () => void;
  renameConversation: (conversationId: string, name: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<boolean>;
  retryMessages: () => Promise<void>;
  clearMessagesError: () => void;
  refreshHistory: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
  handleExportTxt: () => void;
  handleExportPdf: () => Promise<void>;
  handleCopyToClipboard: () => void;
}

const ChatContext = createContext<ChatContextValue | undefined>(undefined);

/**
 * The context is split by *how often each slice changes*, not by feature.
 *
 * `ChatContext` above carries the whole store, so anything reading it
 * re-renders on every keystroke and every streamed chunk. That's correct for
 * the chat panel, which is what those updates are for — but the history
 * sidebar reads five fields that have nothing to do with typing, and was
 * being dragged along for the ride. Compensating for that with memo() and
 * useCallback() worked but was fragile: one inline prop anywhere and the
 * whole arrangement silently stops working.
 *
 * These two extra contexts make it structural instead. A consumer of
 * ChatHistoryContext simply isn't subscribed to `input` or `streamingText`,
 * so it *cannot* re-render for them — no memoization required.
 */
interface ChatHistoryContextValue {
  conversations: ConversationSummary[];
  activeConversationId: string | null;
  isHistoryLoading: boolean;
  historyError: string | null;
  historyNextCursor: string | null;
  isLoadingMoreHistory: boolean;
  isSidebarCollapsed: boolean;
}

/** Every action here is a stable useCallback, so this value never changes identity. */
interface ChatActionsContextValue {
  toggleSidebar: () => void;
  selectConversation: (conversationId: string | null) => Promise<void>;
  startNewConversation: () => void;
  renameConversation: (conversationId: string, name: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<boolean>;
  refreshHistory: () => Promise<void>;
  loadMoreHistory: () => Promise<void>;
}

const ChatHistoryContext = createContext<ChatHistoryContextValue | undefined>(undefined);
const ChatActionsContext = createContext<ChatActionsContextValue | undefined>(undefined);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const { getAccessToken, retryFirstLoginCheck } = useAuth();
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const stateRef = useRef(state);
  const lastMessageIdRef = useRef<string | null>(null);
  const isRefreshingHistoryRef = useRef(false);
  const isLoadingMoreHistoryRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const setInput = useCallback((value: string) => {
    dispatch({ type: 'SET_INPUT', payload: value });
  }, []);

  const clearSendError = useCallback(() => {
    dispatch({ type: 'SET_SEND_ERROR', payload: null });
  }, []);

  const clearMessagesError = useCallback(() => {
    dispatch({ type: 'SET_MESSAGES_ERROR', payload: null });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_SIDEBAR' });
  }, []);

  const updateConversationList = useCallback(() => {
    const list = chatHistoryService.listConversations();
    const activeId = stateRef.current.activeConversationId;

    dispatch({ type: 'SET_CONVERSATIONS', payload: list });

    if (activeId && !list.some((conversation) => conversation.conversationId === activeId)) {
      dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: null });
      dispatch({ type: 'RESET_SESSION' });
      lastMessageIdRef.current = null;
    }

    return list;
  }, []);

  const refreshHistory = useCallback(async () => {
    if (isRefreshingHistoryRef.current) {
      return;
    }

    isRefreshingHistoryRef.current = true;
    dispatch({ type: 'SET_HISTORY_LOADING', payload: true });
    dispatch({ type: 'SET_HISTORY_ERROR', payload: null });

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Failed to obtain access token');
      }

      const { history: backendHistory, nextCursor } = await chatHistoryService.fetchFromBackend(token, {
        limit: HISTORY_PAGE_LIMIT,
      });
      chatHistoryService.syncWithBackendHistory(backendHistory);
      dispatch({ type: 'SET_HISTORY_NEXT_CURSOR', payload: nextCursor });
      updateConversationList();
    } catch (error) {
      console.error('[ChatStore] Error refreshing history:', error);
      const message = error instanceof Error ? error.message : 'Failed to load history';
      dispatch({ type: 'SET_HISTORY_ERROR', payload: message });
      updateConversationList();
    } finally {
      dispatch({ type: 'SET_HISTORY_LOADING', payload: false });
      isRefreshingHistoryRef.current = false;
    }
  }, [getAccessToken, updateConversationList]);

  const loadMoreHistory = useCallback(async () => {
    const cursor = stateRef.current.historyNextCursor;
    if (isLoadingMoreHistoryRef.current || !cursor) {
      return;
    }

    isLoadingMoreHistoryRef.current = true;
    dispatch({ type: 'SET_LOADING_MORE_HISTORY', payload: true });

    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Failed to obtain access token');
      }

      const { history: backendHistory, nextCursor } = await chatHistoryService.fetchFromBackend(token, {
        limit: HISTORY_PAGE_LIMIT,
        cursor,
      });
      chatHistoryService.mergeBackendHistory(backendHistory);
      dispatch({ type: 'SET_HISTORY_NEXT_CURSOR', payload: nextCursor });
      updateConversationList();
    } catch (error) {
      // Deliberately doesn't touch `historyError`: that field drives the
      // sidebar's full-panel error screen (see ChatHistorySidebar), and a
      // failed "Load more" must not hide the conversations already on
      // screen. The cursor is untouched, so the button just reappears and
      // the user can retry.
      console.error('[ChatStore] Error loading more history:', error);
    } finally {
      dispatch({ type: 'SET_LOADING_MORE_HISTORY', payload: false });
      isLoadingMoreHistoryRef.current = false;
    }
  }, [getAccessToken, updateConversationList]);

  const loadConversationMessages = useCallback(
    async (conversationId: string) => {
      dispatch({ type: 'SET_MESSAGES_LOADING', payload: true });
      dispatch({ type: 'SET_MESSAGES_ERROR', payload: null });

      try {
        let record = chatHistoryService.getConversation(conversationId);

        if (!record || record.messages.length === 0) {
          const token = await getAccessToken();
          if (!token) {
            throw new Error('Failed to obtain access token');
          }

          record = await chatHistoryService.fetchConversationById(conversationId, token);
          chatHistoryService.saveConversation(record);
          updateConversationList();
        }

        if (!record) {
          throw new Error('Conversation not found');
        }

        const messages = record.messages.map(storedToMessage);
        dispatch({ type: 'SET_MESSAGES', payload: messages });
        dispatch({ type: 'SET_CONVERSATION_ID', payload: record.conversationId });
        dispatch({ type: 'SET_INPUT', payload: '' });
        dispatch({ type: 'SET_SEND_ERROR', payload: null });
        lastMessageIdRef.current = record.lastMessageId ?? null;

        if (record.tokenCount !== undefined) {
          dispatch({
            type: 'SET_TOKEN_INFO',
            payload: {
              tokenCount: record.tokenCount,
              isExhausted: record.isExhausted ?? false,
            },
          });
        } else {
          // Backend GET does not return conversation_token_count, so estimate
          // from stored message contents. This approximation is replaced by the
          // accurate backend value the next time the user sends a message.
          const estimated = record.messages.reduce(
            (total, msg) => total + estimateTokens(msg.content),
            0,
          );
          if (estimated > 0) {
            dispatch({
              type: 'SET_TOKEN_INFO',
              payload: { tokenCount: estimated, isExhausted: false },
            });
          }
        }
      } catch (error) {
        console.error('[ChatStore] Error loading conversation messages:', error);
        const message = error instanceof Error ? error.message : 'Failed to load messages';
        dispatch({ type: 'SET_MESSAGES_ERROR', payload: message });
      } finally {
        dispatch({ type: 'SET_MESSAGES_LOADING', payload: false });
      }
    },
    [getAccessToken, updateConversationList],
  );

  const startNewConversation = useCallback(() => {
    dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: null });
    dispatch({ type: 'RESET_SESSION' });
    lastMessageIdRef.current = null;
  }, []);

  const selectConversation = useCallback(
    async (conversationId: string | null) => {
      if (!conversationId) {
        startNewConversation();
        return;
      }

      dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
      dispatch({ type: 'RESET_SESSION' });
      await loadConversationMessages(conversationId);
    },
    [loadConversationMessages, startNewConversation],
  );

  const retryMessages = useCallback(async () => {
    const activeId = stateRef.current.activeConversationId;
    if (activeId) {
      await loadConversationMessages(activeId);
    }
  }, [loadConversationMessages]);

  const renameConversation = useCallback(
    async (conversationId: string, name: string) => {
      dispatch({ type: 'SET_HISTORY_ERROR', payload: null });

      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Failed to obtain access token');
        }

        const backendRecord = await chatHistoryService.renameConversationRemote(conversationId, name, token);
        const resolvedName = backendRecord?.name || name;
        chatHistoryService.renameConversation(conversationId, resolvedName);
        updateConversationList();
      } catch (error) {
        console.error('[ChatStore] Error renaming conversation:', error);
        const message = error instanceof Error ? error.message : 'Unknown error while renaming the conversation';
        dispatch({ type: 'SET_HISTORY_ERROR', payload: message });
        throw error;
      }
    },
    [getAccessToken, updateConversationList],
  );

  const deleteConversation = useCallback(
    async (conversationId: string) => {
      dispatch({ type: 'SET_HISTORY_ERROR', payload: null });

      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error('Failed to obtain access token');
        }

        await chatHistoryService.deleteConversationRemote(conversationId, token);
        const deleted = chatHistoryService.deleteConversation(conversationId);

        if (deleted) {
          updateConversationList();
        }

        return deleted;
      } catch (error) {
        console.error('[ChatStore] Error deleting conversation:', error);
        const message = error instanceof Error ? error.message : 'Unknown error while deleting the conversation';
        dispatch({ type: 'SET_HISTORY_ERROR', payload: message });
        throw error;
      }
    },
    [getAccessToken, updateConversationList],
  );

  const sendMessage = useCallback(
    async (rawMessage: string) => {
      const trimmed = rawMessage.trim();
      const currentState = stateRef.current;

      if (!trimmed || currentState.isSending || currentState.isExhausted) {
        return;
      }

      const userMessage: Message = {
        role: 'user',
        content: trimmed,
        createdAt: new Date().toISOString(),
      };

      const messagesAfterUser = [...currentState.messages, userMessage];

      dispatch({ type: 'APPEND_MESSAGE', payload: userMessage });
      dispatch({ type: 'SET_INPUT', payload: '' });
      dispatch({ type: 'SET_SENDING', payload: true });
      dispatch({ type: 'SET_SEND_ERROR', payload: null });
      dispatch({ type: 'SET_MESSAGES_ERROR', payload: null });

      if (currentState.conversationId) {
        chatHistoryService.appendMessage(currentState.conversationId, userMessage);
        updateConversationList();
      }


      // Throttle buffer for streamed text (see STREAM_FLUSH_MS).
      let pendingChunk = '';
      let flushTimer: ReturnType<typeof setTimeout> | null = null;

      const flushStreamBuffer = () => {
        flushTimer = null;
        if (pendingChunk) {
          dispatch({ type: 'APPEND_STREAMING_TEXT', payload: pendingChunk });
          pendingChunk = '';
        }
      };

      // Drop anything still buffered. Must run before CLEAR_STREAMING —
      // a timer firing after it would re-populate streamingText and leave a
      // ghost streaming bubble under the finished message.
      const cancelStreamBuffer = () => {
        if (flushTimer !== null) {
          clearTimeout(flushTimer);
          flushTimer = null;
        }
        pendingChunk = '';
      };

      try {
        const startTime = Date.now();
        const accessToken = await getAccessToken();
        if (!accessToken) {
          throw new Error('Failed to obtain access token');
        }

        let accumulatedText = '';

        const response = await chatService.sendStreamingMessage(
          trimmed,
          currentState.conversationId,
          lastMessageIdRef.current,
          accessToken,
          (chunk) => {
            if (stateRef.current.queuedMessage) dispatch({ type: 'SET_QUEUED_MESSAGE', payload: null });
            accumulatedText += chunk;
            pendingChunk += chunk;
            if (flushTimer === null) {
              flushTimer = setTimeout(flushStreamBuffer, STREAM_FLUSH_MS);
            }
          },
          (status) => {
            if (stateRef.current.queuedMessage) dispatch({ type: 'SET_QUEUED_MESSAGE', payload: null });
            dispatch({ type: 'SET_AGENT_STATUS', payload: status });
          },
          (msg) => dispatch({ type: 'SET_QUEUED_MESSAGE', payload: msg }),
        );

        cancelStreamBuffer();
        dispatch({ type: 'CLEAR_STREAMING' });

        let conversationId = currentState.conversationId;
        if (!conversationId && response.conversation_id) {
          conversationId = response.conversation_id;
          dispatch({ type: 'SET_CONVERSATION_ID', payload: conversationId });
          dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: conversationId });
        }

        lastMessageIdRef.current = response.last_message_id ?? null;

        if (response.conversation_token_count !== undefined) {
          dispatch({
            type: 'SET_TOKEN_INFO',
            payload: {
              tokenCount: response.conversation_token_count,
              isExhausted: response.exhausted ?? false,
            },
          });
        }

        const assistantMessage: Message = {
          role: 'assistant',
          content: accumulatedText || response.answer || response.response || 'No response received',
          createdAt: new Date().toISOString(),
          responseTime: Date.now() - startTime,
        };

        dispatch({ type: 'APPEND_MESSAGE', payload: assistantMessage });

        const allMessages = [...messagesAfterUser, assistantMessage];

        if (conversationId) {
          const appendedRecord = chatHistoryService.appendMessage(conversationId, assistantMessage);
          if (appendedRecord) {
            const recordWithLastId = {
              ...appendedRecord,
              lastMessageId: response.last_message_id,
              ...(response.conversation_token_count !== undefined && {
                tokenCount: response.conversation_token_count,
                isExhausted: response.exhausted ?? false,
              }),
            };
            chatHistoryService.saveConversation(recordWithLastId);
          } else {
            const autoName = buildConversationName(allMessages);
            chatHistoryService.createConversation({
              conversationId,
              name: autoName,
              messages: allMessages,
              lastMessageId: response.last_message_id,
              ...(response.conversation_token_count !== undefined && {
                tokenCount: response.conversation_token_count,
                isExhausted: response.exhausted ?? false,
              }),
            });
            if (autoName !== 'New conversation') {
              void chatHistoryService.renameConversationRemote(conversationId, autoName, accessToken);
            }
          }
        } else {
          const autoName = buildConversationName(allMessages);
          const createdRecord = chatHistoryService.createConversation({
            conversationId: response.conversation_id,
            name: autoName,
            messages: allMessages,
            lastMessageId: response.last_message_id,
            ...(response.conversation_token_count !== undefined && {
              tokenCount: response.conversation_token_count,
              isExhausted: response.exhausted ?? false,
            }),
          });
          dispatch({ type: 'SET_CONVERSATION_ID', payload: createdRecord.conversationId });
          dispatch({ type: 'SET_ACTIVE_CONVERSATION', payload: createdRecord.conversationId });
          if (autoName !== 'New conversation') {
            void chatHistoryService.renameConversationRemote(createdRecord.conversationId, autoName, accessToken);
          }
        }

        updateConversationList();
      } catch (error) {
        console.error('[ChatStore] Error sending message:', error);
        dispatch({ type: 'CLEAR_STREAMING' });
        if (error instanceof ForbiddenError) {
          // Account suspended or access revoked — re-check profile so ProtectedRoute
          // shows the suspended/access-denied screen instead of a generic chat error.
          await retryFirstLoginCheck();
          return;
        }
        const message =
          error instanceof ChatError
            ? error.message
            : error instanceof Error
              ? error.message
              : 'An unexpected error occurred. Please try again.';
        dispatch({ type: 'SET_SEND_ERROR', payload: message });
      } finally {
        cancelStreamBuffer();
        dispatch({ type: 'SET_SENDING', payload: false });
      }
    },
    [getAccessToken, updateConversationList],
  );

  useEffect(() => {
    void refreshHistory();
  }, [refreshHistory]);

  const { handleExportTxt, handleExportPdf, handleCopyToClipboard } = useChatExport(state.messages);

  // Memoized so consumers only re-render when the state they read actually
  // changed — every action below is already a stable useCallback, so `state`
  // is the only dependency that moves during a streaming response.
  const value: ChatContextValue = useMemo(() => ({
    ...state,
    setInput,
    clearSendError,
    sendMessage,
    toggleSidebar,
    selectConversation,
    startNewConversation,
    renameConversation,
    deleteConversation,
    retryMessages,
    clearMessagesError,
    refreshHistory,
    loadMoreHistory,
    handleExportTxt,
    handleExportPdf,
    handleCopyToClipboard,
  }), [
    state,
    setInput,
    clearSendError,
    sendMessage,
    toggleSidebar,
    selectConversation,
    startNewConversation,
    renameConversation,
    deleteConversation,
    retryMessages,
    clearMessagesError,
    refreshHistory,
    loadMoreHistory,
    handleExportTxt,
    handleExportPdf,
    handleCopyToClipboard,
  ]);

  // Only moves when the history itself moves — never on typing or streaming.
  const historyValue: ChatHistoryContextValue = useMemo(() => ({
    conversations: state.conversations,
    activeConversationId: state.activeConversationId,
    isHistoryLoading: state.isHistoryLoading,
    historyError: state.historyError,
    historyNextCursor: state.historyNextCursor,
    isLoadingMoreHistory: state.isLoadingMoreHistory,
    isSidebarCollapsed: state.isSidebarCollapsed,
  }), [
    state.conversations,
    state.activeConversationId,
    state.isHistoryLoading,
    state.historyError,
    state.historyNextCursor,
    state.isLoadingMoreHistory,
    state.isSidebarCollapsed,
  ]);

  // All stable callbacks — in practice this value is created once and never again.
  const actionsValue: ChatActionsContextValue = useMemo(() => ({
    toggleSidebar,
    selectConversation,
    startNewConversation,
    renameConversation,
    deleteConversation,
    refreshHistory,
    loadMoreHistory,
  }), [
    toggleSidebar,
    selectConversation,
    startNewConversation,
    renameConversation,
    deleteConversation,
    refreshHistory,
    loadMoreHistory,
  ]);

  return (
    <ChatActionsContext.Provider value={actionsValue}>
      <ChatHistoryContext.Provider value={historyValue}>
        <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
      </ChatHistoryContext.Provider>
    </ChatActionsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChatHistory = (): ChatHistoryContextValue => {
  const context = useContext(ChatHistoryContext);
  if (context === undefined) {
    throw new Error('useChatHistory must be used within a ChatProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChatActions = (): ChatActionsContextValue => {
  const context = useContext(ChatActionsContext);
  if (context === undefined) {
    throw new Error('useChatActions must be used within a ChatProvider');
  }
  return context;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChat = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
